<template>
  <!--
    The password form of a locked JobShop (JET-2155), rendered by app.vue in place
    of the whole layout. A plain HTML form on purpose: it posts back to the URL it
    was shown on and works before (or without) hydration. The server answers 303
    to the same URL on success, or renders this again with `error` set.
  -->
  <div class="password-gate" :style="themeStyles">
    <main class="gate-main">
      <div class="gate-brand">
        <img
          v-if="branding?.logoUrl"
          class="gate-logo"
          :src="branding.logoUrl"
          :alt="branding?.title || ''"
        >
        <span v-else-if="branding?.title" class="gate-name">{{ branding.title }}</span>
      </div>

      <form
        class="gate-form"
        method="post"
        :action="action"
      >
        <span class="gate-badge" aria-hidden="true">
          <svg
            width="22"
            height="22"
            viewBox="0 0 22 22"
            fill="none"
          >
            <rect
              x="4"
              y="10"
              width="14"
              height="9"
              rx="2"
              stroke="currentColor"
              stroke-width="1.8"
            />
            <path d="M7.5 10V7a3.5 3.5 0 0 1 7 0v3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
          </svg>
        </span>

        <div class="gate-text">
          <h1 class="gate-heading">
            {{ t("password-gate.heading") }}
          </h1>
          <p class="gate-intro">
            {{ t("password-gate.intro") }}
          </p>
        </div>

        <!-- Not the visitor's mistake, so not tied to the field. -->
        <div v-if="noticeKey" role="alert" class="gate-notice">
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            aria-hidden="true"
          >
            <path d="M9 2.5 16 15H2L9 2.5Z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" />
            <path d="M9 7.5v3M9 12.5v.3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
          </svg>
          <p>{{ t(noticeKey) }}</p>
        </div>

        <input type="hidden" :name="SUBMISSION_FIELD" value="1">

        <div class="gate-field">
          <CRFieldLabel
            :id="PASSWORD_FIELD"
            :name="PASSWORD_FIELD"
            :label="t('password-gate.label')"
          />
          <div class="gate-input" :class="{ 'is-invalid': isInvalid }">
            <input
              :id="PASSWORD_FIELD"
              class="gate-input-field"
              :type="isRevealed ? 'text' : 'password'"
              :name="PASSWORD_FIELD"
              autocomplete="current-password"
              required
              autofocus
              :aria-invalid="isInvalid ? 'true' : 'false'"
              :aria-describedby="isInvalid ? errorId : undefined"
            >
            <!-- Only once hydrated: before that it could not do anything. -->
            <button
              v-if="isHydrated"
              type="button"
              class="gate-reveal"
              :aria-label="t('password-gate.show-password')"
              :aria-pressed="isRevealed ? 'true' : 'false'"
              :aria-controls="PASSWORD_FIELD"
              @click="isRevealed = !isRevealed"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 22 22"
                fill="none"
                aria-hidden="true"
              >
                <path d="M2 11s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6Z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round" />
                <circle
                  cx="11"
                  cy="11"
                  r="2.8"
                  stroke="currentColor"
                  stroke-width="1.7"
                />
                <path v-if="isRevealed" d="M4 18 18 4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
              </svg>
            </button>
          </div>
          <p
            v-if="isInvalid"
            :id="errorId"
            role="alert"
            class="field-error gate-error"
          >
            {{ t("password-gate.error-invalid") }}
          </p>
        </div>

        <button type="submit" class="gate-submit">
          {{ t("password-gate.submit") }}
        </button>
      </form>
    </main>
  </div>
</template>

<script setup lang="ts">
import { PASSWORD_FIELD, SUBMISSION_FIELD } from "@/utils/passwordGateFields"
import { NOINDEX_NOFOLLOW } from "@/utils/robotsDirectives"
import { resolveI18nLocale } from "@/utils/resolveI18nLocale"

const props = defineProps<{
  error: PasswordGateError | null
}>()

// Everything reaching getCurrentInstance() before the first await (AGENTS.md).
const { t, setLocale, locales } = useI18n()
const requestUrl = useRequestURL()

// Posts back to the very URL the form is shown on; the server only ever
// redirects there, so there is no return parameter to tamper with.
const action = `${requestUrl.pathname}${requestUrl.search}`
const errorId = `${PASSWORD_FIELD}-error`

const branding = await usePasswordGateBranding()

// The JobShop's language, like layouts/default.vue — not the visitor's browser.
await setLocale(resolveI18nLocale(branding.value?.locale, locales.value.map(l => l.code), "en"))

// Unbranded (null) falls back to the crossroads defaults in both. No font SRI
// manifest: its flag is evaluated per tenant, and a locked render keeps the
// tenant out of the header store (plugins/header-store.server.ts) — which also
// keeps that store, empty or not, out of the payload.
useFonts(branding.value)
useHeadingFontClass(branding.value)

useHeadSafe({
  htmlAttrs: { lang: branding.value?.locale || undefined },
  title: branding.value?.title ? `${t("password-gate.title")} – ${branding.value.title}` : t("password-gate.title"),
  link: branding.value?.faviconUrl ? [{ rel: "icon", href: branding.value.faviconUrl }] : [],
})

// The header says the same (server/utils/passwordGate.ts); the tag covers
// anything that only reads the markup.
useSeoMeta({ robots: NOINDEX_NOFOLLOW })

// A wrong password is the field's error; the others are a notice above it.
const isInvalid = computed(() => props.error === "invalid")

const NOTICE_KEYS: Partial<Record<PasswordGateError, string>> = {
  "unavailable": "password-gate.error-unavailable",
  "rate-limited": "password-gate.error-rate-limited",
}
const noticeKey = computed(() => props.error ? NOTICE_KEYS[props.error] : undefined)

const isRevealed = ref(false)
const isHydrated = ref(false)
onMounted(() => {
  isHydrated.value = true
})

// Only colours the JobShop set; everything else keeps the stylesheet defaults.
const themeStyles = computed(() => {
  const { button } = branding.value ?? {}

  return Object.fromEntries(Object.entries({
    "--gate-button-background": button?.backgroundColor,
    "--gate-button-text": button?.textColor,
  }).filter(([, value]) => value))
})
</script>

<style lang="scss" scoped>
@import "./@jobshop/crossroads/components/Forms/styles/field-shared";

.password-gate {
  --gate-accent: var(--gate-button-background, #1B2430);

  display: flex;
  min-height: 100dvh;
  background: #F3F4F2;

  @media (width < $breakpoint-tablet) {
    background: #FFF;
  }
}

.gate-main {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 28px;
  align-items: center;
  justify-content: center;

  padding: 48px 16px;

  @media (width < $breakpoint-tablet) {
    align-items: stretch;
    justify-content: flex-start;
    padding: 32px 20px;
  }
}

.gate-logo {
  max-width: 200px;
  max-height: 48px;
  object-fit: contain;
}

.gate-name {
  @include font-family("heading");

  font-size: 20px;
  font-weight: var(--cr-heading-font-weight, 700);
  color: #1B2430;
}

.gate-form {
  display: flex;
  flex-direction: column;
  gap: 20px;

  box-sizing: border-box;
  width: 100%;
  max-width: 460px;
  padding: 40px;

  background: #FFF;
  border: 1px solid #DDE1E4;
  border-radius: var(--cr-border-radius-large, 16px);

  // On a phone the form is the page: no card around it.
  @media (width < $breakpoint-tablet) {
    max-width: none;
    padding: 0;
    border: 0;
  }
}

.gate-badge {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 48px;
  height: 48px;

  color: var(--gate-accent);

  background: color-mix(in srgb, var(--gate-accent) 12%, #FFF);
  border-radius: 50%;
}

.gate-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.gate-heading {
  @include font-family("heading");

  margin: 0;
  font-size: 26px;
  font-weight: var(--cr-heading-font-weight, 700);
  line-height: 1.25;
}

.gate-intro {
  @include font-family("body");

  margin: 0;
  line-height: 1.5;
}

.gate-notice {
  @include font-family("body");

  display: flex;
  gap: 10px;
  align-items: flex-start;

  padding: 12px 14px;

  color: #6B3410;

  background: #FEF3E7;
  border: 1px solid #F1C8A0;
  border-radius: 8px;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }

  p {
    margin: 0;
    line-height: 1.45;
  }
}

.gate-input {
  display: flex;
  align-items: center;

  box-sizing: border-box;
  height: 48px;

  background: var(--cr-form-field-background, #FFF);
  border: 1px solid var(--cr-form-field-border-color, #C9CED4);
  border-radius: 6px;

  &:focus-within {
    border-color: var(--cr-form-focus-color, var(--gate-accent));
    outline: 2px solid var(--cr-form-focus-color, var(--gate-accent));
    outline-offset: -1px;
  }

  &.is-invalid {
    border: 2px solid var(--cr-form-error-color, #B42318);
  }
}

.gate-input-field {
  @include font-family("body");

  flex-grow: 1;

  min-width: 0;
  height: 100%;
  padding: 0 14px;

  font-size: 16px;
  color: var(--cr-form-field-text-color, inherit);

  background: transparent;
  border: 0;
  outline: none;
}

.gate-reveal {
  cursor: pointer;

  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

  width: 44px;
  height: 44px;

  color: #3D4753;

  background: transparent;
  border: 0;

  &:focus-visible {
    outline: 2px solid var(--gate-accent);
    outline-offset: -4px;
  }
}

.gate-error {
  margin: 4px 0 0;
  color: var(--cr-form-error-color, #B42318);
}

.gate-submit {
  @include font-family("button");

  cursor: pointer;

  height: 48px;
  padding: 0 24px;

  font-size: 16px;
  font-weight: var(--cr-button-font-weight, 600);
  color: var(--gate-button-text, #FFF);

  background: var(--gate-accent);
  border: 0;
  border-radius: 6px;

  &:hover {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 2px solid var(--gate-accent);
    outline-offset: 2px;
  }
}
</style>
